/**
 * O eixo do tempo e as alças da reta da dissipação (onda 313, R11; spec 07 §5.5) — contas puras.
 *
 * A curva de dissipação se lê em √t (onde a reta do trecho inicial é reta), em log t ou em t. A reta do
 * motor (`Reta`, dois pontos em (√t, u)) se ajusta por duas alças, só na vista √t, com o mesmo gesto do
 * Taylor do adensamento (o `Plano` da bancada): a alça cola na leitura mais próxima (o ímã) e o Shift a
 * solta. A reta que o `.cpt` grava tem as pontas fora de qualquer janela (x = ±10.000): as alças vão
 * para dentro da janela visível SEM mudar a reta — os dois pontos são da mesma reta, a 10 % e a 60 % da
 * janela.
 */
import type { Reta } from '../motor';

/**
 * O que a tela acrescenta ao t₅₀ (sempre «pela reta», regra `dis.t50-pela-reta`) quando a curva medida não
 * chega a u₅₀ — a frase do R11, igual na tela, no PDF (315) e no Portal (317).
 */
export const FRASE_T50_EXTRAPOLADO = 'extrapolado — a curva não chegou a u₅₀';

/** A vista do tempo: √t, log t ou t. */
export type EixoDoTempo = 'raiz' | 'log' | 'linear';

/** O x de um tempo (s) na vista: √t; log₁₀ t (t ≤ 0 não tem lugar: NaN); t. O que não existe é NaN. */
export function xDoTempo(t: number, eixo: EixoDoTempo): number {
  if (!Number.isFinite(t)) return NaN;
  if (eixo === 'raiz') return t >= 0 ? Math.sqrt(t) : NaN;
  if (eixo === 'log') return t > 0 ? Math.log10(t) : NaN;
  return t;
}

/** Um ponto do plano da curva (em √t e u, ou em pixel, conforme quem chama). */
export interface PontoDoPlano {
  x: number;
  y: number;
}

/** O u da reta em √t = x. */
const naReta = (reta: Reta, x: number): number => reta.y1 + ((x - reta.x1) * (reta.y2 - reta.y1)) / (reta.x2 - reta.x1);

/**
 * As duas alças de uma reta numa janela de √t: a 10 % e a 60 % da janela, sobre a mesma reta (a reta não
 * muda: `retaPelasAlcas` das duas devolve a mesma inclinação e o mesmo uᵢ).
 */
export function alcasDaReta(reta: Reta, janela: [number, number]): [PontoDoPlano, PontoDoPlano] {
  const [j0, j1] = janela;
  const xa = j0 + 0.1 * (j1 - j0);
  const xb = j0 + 0.6 * (j1 - j0);
  return [{ x: xa, y: naReta(reta, xa) }, { x: xb, y: naReta(reta, xb) }];
}

/** A reta pelas duas alças (em √t e u) — a que vai ao motor e à API. */
export function retaPelasAlcas(a: PontoDoPlano, b: PontoDoPlano): Reta {
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
}

/**
 * As alças de quem arrasta e a reta do pai no último passo delas (a que o pai tinha quando o engenheiro
 * mexeu, ou a que ele devolveu depois passando por elas).
 */
export interface AlcasLocais {
  alcas: [PontoDoPlano, PontoDoPlano];
  retaDoPai: Reta;
}

/** As duas retas são a mesma (os quatro números). */
const mesmaReta = (a: Reta, b: Reta): boolean => a.x1 === b.x1 && a.y1 === b.y1 && a.x2 === b.x2 && a.y2 === b.y2;

/** O ponto está sobre a reta (a menos do arredondamento). */
const sobre = (reta: Reta, q: PontoDoPlano): boolean => Math.abs(naReta(reta, q.x) - q.y) <= 1e-6 * Math.max(1, Math.abs(q.y));

/**
 * Que alças e que reta a curva mostra (R11): a tela recalcula a reta uns 150 ms depois da última mudança,
 * e as alças não podem pular nesse meio-tempo nem voltar depois de descartadas.
 *  · sem alças locais: as da reta do pai, a 10 % e 60 % da janela;
 *  · arrastando: as locais, e a reta por elas;
 *  · sem arrasto, o pai ainda com a reta do último passo (`retaDoPai`: ele não respondeu): as locais;
 *  · sem arrasto, o pai devolveu uma reta que passa pelas duas alças (a resposta dele): as locais, e
 *    `retaDoPai` passa a ser essa reta — dali em diante, só ela segura as alças;
 *  · sem arrasto, o pai mudou para outra reta (sair sem gravar, voltar ao gravado, a reta automática): as
 *    alças locais caem (`local` null) e valem as da reta do pai.
 * `local` devolvido é o mesmo objeto quando nada muda (quem guarda em estado compara por identidade).
 */
export function alcasVigentes(x: {
  local: AlcasLocais | null;
  retaDoPai: Reta;
  arrastando: boolean;
  janela: [number, number];
}): { alcas: [PontoDoPlano, PontoDoPlano]; reta: Reta; local: AlcasLocais | null } {
  const { local, retaDoPai } = x;
  const doPai = { alcas: alcasDaReta(retaDoPai, x.janela), reta: retaDoPai, local: null };
  if (!local) return doPai;
  const dasLocais = (l: AlcasLocais) => ({ alcas: l.alcas, reta: retaPelasAlcas(l.alcas[0], l.alcas[1]), local: l });
  if (x.arrastando || mesmaReta(retaDoPai, local.retaDoPai)) return dasLocais(local);
  if (sobre(retaDoPai, local.alcas[0]) && sobre(retaDoPai, local.alcas[1])) return dasLocais({ alcas: local.alcas, retaDoPai });
  return doPai;
}

/**
 * O ímã da alça, em pixel: o ponto da curva mais próximo do alvo, se estiver a até `raio` e a alça não
 * estiver solta (Shift); senão, o próprio alvo. Os pontos sem número da curva ficam de fora.
 */
export function imantar(
  alvo: PontoDoPlano,
  curva: { x: ArrayLike<number>; y: ArrayLike<number> },
  raio: number,
  solto: boolean,
): { x: number; y: number; imantado: boolean } {
  if (solto) return { x: alvo.x, y: alvo.y, imantado: false };
  let melhor = -1;
  let distancia = Infinity;
  const n = Math.min(curva.x.length, curva.y.length);
  for (let i = 0; i < n; i++) {
    const dx = curva.x[i] - alvo.x;
    const dy = curva.y[i] - alvo.y;
    const d = Math.hypot(dx, dy);
    if (Number.isFinite(d) && d < distancia) {
      distancia = d;
      melhor = i;
    }
  }
  if (melhor < 0 || distancia > raio) return { x: alvo.x, y: alvo.y, imantado: false };
  return { x: curva.x[melhor], y: curva.y[melhor], imantado: true };
}
